import { useLayoutEffect, type RefObject } from 'react'
import { Field, Label, Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  AdjustmentsHorizontalIcon,
  ArrowRightIcon,
  BookOpenIcon,
  FolderIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../providers/auth/useAuth'
import type { ChatSettings } from '../../providers/chat/context'
import PlanToggle from '../chat/PlanToggle'
import Toggle from '../ui/Toggle'
import AttachmentPicker from './AttachmentPicker'
import ComposerAttachments from './ComposerAttachments'
import type { ComposerUploads } from './useComposerAttachments'

const MAX_LENGTH = 8000
const MAX_HEIGHT = 288

type Props = {
  value: string
  onChange: (value: string) => void
  uploads: ComposerUploads
  textareaRef: RefObject<HTMLTextAreaElement | null>
  onSubmit: () => void
  settings: ChatSettings
  onSettingsChange: (settings: ChatSettings) => void
  disabled?: boolean
  placeholder?: string
}

export default function PromptComposer({
  value,
  onChange,
  uploads,
  textareaRef,
  onSubmit,
  settings,
  onSettingsChange,
  disabled = false,
  placeholder = 'Ask a question or provide a task...',
}: Props) {
  const { brand } = useAuth()
  const brandBookOn = brand?.brand_book_enabled ?? true
  const overLimit = value.length > MAX_LENGTH
  const canSubmit =
    (value.trim().length > 0 || uploads.attachments.length > 0) && !uploads.uploading && !overLimit && !disabled

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT)}px`
  }, [value, textareaRef])

  function clear() {
    onChange('')
    textareaRef.current?.focus()
  }

  return (
    <div
      {...uploads.dropZone}
      className={`relative mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-[0_4px_12px_-4px_rgba(15,23,42,0.06),0_18px_44px_-20px_rgba(15,23,42,0.22)] transition-all duration-200 focus-within:border-purple-400 focus-within:shadow-[0_6px_16px_-6px_rgba(126,34,206,0.22),0_24px_56px_-20px_rgba(126,34,206,0.28)] ${
        uploads.dragging ? 'ring-2 ring-purple-400' : ''
      }`}
    >
      <ComposerAttachments uploads={uploads} />

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onPaste={uploads.paste}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            if (canSubmit) onSubmit()
          }
        }}
        className="block max-h-72 w-full resize-none overflow-y-auto bg-transparent px-5 pt-5 pb-2 text-base leading-snug text-slate-900 [scrollbar-width:thin] placeholder:text-slate-400 focus:outline-none sm:text-sm"
      />

      <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5">
        <div className="flex items-center gap-1">
          <AttachmentPicker disabled={disabled} uploads={uploads} />

          <Popover>
            <PopoverButton
              aria-label="Chat settings"
              className="flex size-8 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-700 sm:size-6"
            >
              <AdjustmentsHorizontalIcon className="size-4 sm:size-3.5" />
            </PopoverButton>

            <PopoverPanel
              anchor={{ to: 'top start', gap: 6 }}
              className="z-50 w-64 rounded-xl border border-stone-200 bg-white p-1.5 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)]"
            >
              <Field disabled={!brandBookOn} className="flex items-center gap-2.5 rounded-md px-3 py-2.5 md:py-2">
                <BookOpenIcon className="size-4 shrink-0 text-stone-500" />
                <Label className="min-w-0 grow cursor-pointer text-[13px] text-stone-700 data-disabled:cursor-not-allowed">
                  Brand Book
                  {!brandBookOn && <span className="block text-[11px] text-stone-500">Turned off in Brand Book</span>}
                </Label>
                <Toggle
                  small
                  checked={settings.useBrandBook && brandBookOn}
                  onChange={(useBrandBook) => onSettingsChange({ ...settings, useBrandBook })}
                />
              </Field>
              <Field className="flex items-center gap-2.5 rounded-md px-3 py-2.5 md:py-2">
                <FolderIcon className="size-4 shrink-0 text-stone-500" />
                <Label className="min-w-0 grow cursor-pointer text-[13px] text-stone-700">Files</Label>
                <Toggle
                  small
                  checked={settings.useFiles}
                  onChange={(useFiles) => onSettingsChange({ ...settings, useFiles })}
                />
              </Field>
            </PopoverPanel>
          </Popover>

          <PlanToggle disabled={disabled} />
        </div>

        <div className="flex items-center gap-2">
          {overLimit && (
            <span className="text-[11px] text-red-500">
              {value.length}/{MAX_LENGTH}
            </span>
          )}
          {value.length > 0 && (
            <button
              type="button"
              aria-label="Clear prompt"
              onClick={clear}
              className="flex size-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 sm:size-6"
            >
              <XMarkIcon className="size-4 sm:size-3.5" strokeWidth={2.2} />
            </button>
          )}
          <button
            type="button"
            aria-label="Send message"
            disabled={!canSubmit}
            onClick={onSubmit}
            className="flex size-8 items-center justify-center rounded-full bg-purple-700 text-white transition enabled:hover:bg-purple-800 disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-30 sm:size-6"
          >
            <ArrowRightIcon className="size-4 sm:size-3.5" strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </div>
  )
}
